import React from "react";
import { Loader2, Sparkles } from "lucide-react";
import CreditEstimate from "@/components/billing/CreditEstimate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Enrich-all dialog — three mutually-exclusive modes with live dry-run
// credit estimates. The parent owns `mode` / `creditCap` state so the
// launch handler can read them directly without prop-drilling refs.
//
// When `selectedCount > 0` the dialog collapses the mode picker (mode
// filters are irrelevant — the user hand-picked the rows) and shows a
// scoped explanation instead. The launch is still routed through the
// parent's `onConfirm` handler, which will pass the ids in the API call.
export default function EnrichAllDialog({
  open,
  onClose,
  onConfirm,
  mode,
  setMode,
  estimates,
  creditCap,
  setCreditCap,
  selectedCount = 0,
  selectedEstimate = null,
}) {
  const scoped = selectedCount > 0;
  const modeOptions = [
    {
      value: "new_only",
      title: "New realtors only",
      desc: "Only realtors that have never been scanned before (scan count = 0). Perfect for freshly imported records — avoids re-searching realtors that turned up nothing on past runs.",
    },
    {
      value: "no_socials",
      title: "Only realtors with no socials yet",
      desc: "Skips anyone who already has at least one link. Includes previously-scanned realtors with zero results.",
    },
    {
      value: "fill_gaps",
      title: "Fill missing platforms",
      desc: "Includes realtors missing at least one platform. Existing links are never overwritten — only empty fields get searched.",
    },
  ];
  const est = scoped ? selectedEstimate : estimates ? estimates[mode] : null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="enrich-all-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-slate-700" />
            {scoped
              ? `Enrich ${selectedCount} selected realtor${selectedCount === 1 ? "" : "s"}`
              : "Enrich social profiles"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {scoped ? (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
              data-testid="enrich-all-scoped-note"
            >
              <p className="font-medium">
                Scoped to your selection · {selectedCount} realtor
                {selectedCount === 1 ? "" : "s"}
              </p>
              <p className="text-xs text-emerald-800 mt-0.5">
                Mode filters are skipped for hand-picked rows. Existing social links
                are still never overwritten — only empty fields get searched.
                {est ? (
                  <>
                    {" "}Estimated cost: <b>~{est.estimated_credits} credits</b>.
                  </>
                ) : null}
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              Searches Google/DDG for each realtor&apos;s Facebook, Instagram, LinkedIn,
              TikTok, X, YouTube and Threads. High-confidence hits (name in URL/title)
              auto-save; low-confidence hits go to a review queue.
            </p>
          )}
          {scoped ? null : (
            <div className="space-y-2" data-testid="enrich-all-mode-options">
              {modeOptions.map((opt) => {
                const e = estimates ? estimates[opt.value] : null;
                const active = mode === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setMode(opt.value)}
                    className={`w-full text-left rounded-md border p-3 transition-colors ${
                      active
                        ? "border-[hsl(var(--brand-strong))] bg-red-50/40 ring-1 ring-[hsl(var(--brand-strong))]"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`enrich-all-mode-${opt.value}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-900">{opt.title}</span>
                      {e ? (
                        <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
                          {e.targets} realtors · ~{e.estimated_credits} cr
                        </span>
                      ) : (
                        <Loader2 className="h-3 w-3 animate-spin text-slate-300" />
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                  </button>
                );
              })}
            </div>
          )}
          <div>
            <Label htmlFor="enrich-cap" className="text-xs text-slate-600 font-medium">
              Firecrawl credit budget (0 = no cap)
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                id="enrich-cap"
                type="number"
                min="0"
                max="5000"
                step="50"
                value={creditCap}
                onChange={(e) => setCreditCap(parseInt(e.target.value || "0", 10))}
                data-testid="enrich-all-credit-cap"
                className="w-28"
              />
              <span className="text-xs text-slate-500">
                credits · ~{Math.round(creditCap / 6)} realtors covered
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Each realtor costs ~1 credit per missing platform (+1 broad query).
              The run stops early if this cap is hit.
            </p>
          </div>
        </div>
        <DialogFooter>
          <CreditEstimate action="enrich_all" params={{ count: est?.targets || selectedCount || 1 }} className="mr-auto" />
          <Button variant="ghost" onClick={onClose} data-testid="enrich-all-cancel">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={est ? est.targets === 0 : false}
            data-testid="enrich-all-confirm"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Start enrichment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
